/* iter413g — AI Radce drawer.
 *
 * Slide-in panel from the right that hosts a chat with the Solar Green AI
 * Agent (Claude Sonnet 4.5 via Emergent LLM Key). Available globally:
 *   • top-bar Sparkles icon in admin2 desktop UI
 *   • Cmd+I (Ctrl+I) keyboard shortcut from any /admin route
 *   • dedicated /admin/ai-radce page (uses the full AiAgent.js below)
 *
 * Auto-detects page context (URL + visible entity id from query string) and
 * sends it to the backend so the AI can give situational advice like
 *   "Doporuč next-best-action pro tohoto zákazníka" → resolves to the
 *   customer currently being viewed.
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, X, Bot, Maximize2, Lightbulb, Zap } from "lucide-react";
import { BACKEND_URL } from "../config/api";
import { useNavigate, useLocation } from "react-router-dom";

const API = `${BACKEND_URL}/api/v1/admin`;

/** Detect entity_id from URL — handles both ?id={id} and ?open={id} and /admin/{module}/{id}. */
function detectPageContext(pathname, search) {
  const params = new URLSearchParams(search);
  const id = params.get("id") || params.get("open") || params.get("edit") || "";

  // Map /admin/{module} → entity_type
  const modMap = {
    customers: "customer",
    quotes: "quote",
    projects: "project",
    invoices: "invoice",
    contracts: "contract",
    tasks: "task",
    submissions: "submission",
    inquiries: "inquiry",
    documents: "document",
    "revize-objects": "revize_object",
    "penb-certificates": "penb_certificate",
  };
  const seg = (pathname.split("/")[2] || "").toLowerCase();
  const entityType = modMap[seg] || "";

  // Also handle /admin/customers/:id (no query string)
  let entityId = id;
  if (!entityId && pathname.match(/^\/admin\/[^/]+\/[a-z0-9_]+/)) {
    const segs = pathname.split("/");
    if (segs.length >= 4 && segs[3] !== "new") entityId = segs[3];
  }

  return {
    route: pathname,
    entity_type: entityType,
    entity_id: entityId,
  };
}

export default function AIAdvisorDrawer({ open, onClose, initialPageContext }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(`advisor-${Date.now()}`);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const pageCtx = initialPageContext || detectPageContext(location.pathname, location.search);

  // Greeting message when opening
  useEffect(() => {
    if (!open) return;
    if (messages.length > 0) return;
    let greet = "Ahoj! Jsem AI Radce Solar Green. Můžeš mě:\n• zeptat na cokoliv ze systému\n• požádat o doporučení dalšího kroku\n• spustit hromadný přepočet next-best-action";
    if (pageCtx.entity_type && pageCtx.entity_id) {
      greet += `\n\n📌 Vidím, že prohlížíš ${pageCtx.entity_type} ${pageCtx.entity_id}. Zeptej se mě například: "Co bych měl s tímto klientem dělat dál?"`;
    }
    setMessages([{ role: "assistant", content: greet, ts: Date.now() }]);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]); // eslint-disable-line

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const addMsg = (role, content) =>
    setMessages((p) => [...p, { role, content, ts: Date.now() }]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    addMsg("user", text);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/ai-agent/chat`, {
        message: text,
        session_id: sessionId,
        page_context: pageCtx,
      });
      addMsg("assistant", r.data.response);
    } catch (e) {
      addMsg(
        "assistant",
        `Omlouvám se, něco se pokazilo: ${e.response?.data?.detail || e.message}`
      );
    } finally {
      setLoading(false);
    }
  }, [input, loading, pageCtx, sessionId]);

  const handleAction = async (action) => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/ai-agent/action`, { action: action.type, ...action.payload });
      addMsg("assistant", r.data.message || JSON.stringify(r.data));
      toast.success("Akce provedena");
    } catch (e) {
      toast.error("Chyba: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const suggestions = [
    {
      label: "Přepočítej AI doporučení pro všechny leady",
      icon: Zap,
      onClick: () => handleAction({ type: "recompute_next_actions", payload: { object_id: "all", limit: 2000 } }),
    },
    {
      label: pageCtx.entity_id
        ? `Co s tímto ${pageCtx.entity_type === "customer" ? "zákazníkem" : pageCtx.entity_type}?`
        : "Který lead mám zavolat dnes?",
      icon: Lightbulb,
      onClick: () =>
        setInput(
          pageCtx.entity_id
            ? `Co bych měl udělat s tímto ${pageCtx.entity_type === "customer" ? "zákazníkem" : "záznamem"} dál?`
            : "Který lead mám zavolat dnes a proč?"
        ),
    },
    {
      label: "Otevři plnou stránku",
      icon: Maximize2,
      onClick: () => {
        onClose?.();
        navigate("/admin/ai-agent");
      },
    },
  ];

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none" data-testid="ai-advisor-drawer">
      {/* Click-outside backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px] pointer-events-auto"
        onClick={onClose}
        data-testid="ai-advisor-backdrop"
      />
      {/* Drawer */}
      <div
        className="absolute top-0 right-0 h-full w-full sm:w-[440px] bg-white border-l border-slate-200 shadow-2xl pointer-events-auto flex flex-col"
        style={{ animation: "slideInRight 280ms ease-out" }}
      >
        {/* Header */}
        <div className="flex items-center gap-2.5 px-4 py-3 border-b border-slate-200 bg-gradient-to-br from-violet-50 to-fuchsia-50">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-slate-900">AI Radce</h3>
            <p className="text-[10px] text-slate-500 truncate flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
              Claude Sonnet · {pageCtx.entity_type || "globální"}{pageCtx.entity_id ? ` · ${pageCtx.entity_id.slice(0, 12)}` : ""}
            </p>
          </div>
          <button
            data-testid="ai-advisor-close"
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-white/60 flex items-center justify-center text-slate-500 hover:text-slate-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Quick suggestions */}
        <div className="px-3 py-2 border-b border-slate-100 flex gap-1.5 overflow-x-auto">
          {suggestions.map((s, i) => {
            const Icon = s.icon;
            return (
              <button
                key={i}
                onClick={s.onClick}
                data-testid={`ai-advisor-suggestion-${i}`}
                className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] bg-white border border-slate-200 text-slate-600 hover:bg-violet-50 hover:border-violet-300 hover:text-violet-700 transition-colors"
              >
                <Icon className="w-3 h-3" /> {s.label}
              </button>
            );
          })}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5 bg-slate-50/50">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role === "assistant" && (
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center flex-shrink-0 mt-0.5 mr-1.5">
                  <Bot className="w-3 h-3 text-white" />
                </div>
              )}
              <div
                className={`max-w-[85%] rounded-xl px-3 py-2 text-xs leading-relaxed whitespace-pre-line ${
                  m.role === "user"
                    ? "bg-violet-600 text-white rounded-br-sm"
                    : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm shadow-sm"
                }`}
              >
                {m.content.replace(/\*\*(.*?)\*\*/g, "$1").replace(/\[ACTION:[^\]]+\]/g, "")}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center mr-1.5 flex-shrink-0">
                <Bot className="w-3 h-3 text-white" />
              </div>
              <div className="bg-white border border-slate-200 rounded-xl rounded-bl-sm px-3 py-2 shadow-sm">
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce"></span>
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: "150ms" }}></span>
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: "300ms" }}></span>
                </div>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="px-3 py-2.5 border-t border-slate-200 bg-white">
          <div className="flex items-end gap-2 bg-slate-50 rounded-xl border border-slate-200 focus-within:border-violet-400 transition-colors px-3 py-1.5">
            <textarea
              ref={inputRef}
              data-testid="ai-advisor-input"
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Zeptej se… (Enter=odeslat)"
              className="flex-1 bg-transparent resize-none outline-none text-xs text-slate-800 placeholder-slate-400 max-h-20"
              style={{ minHeight: "24px" }}
            />
            <button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              data-testid="ai-advisor-send"
              className="w-7 h-7 rounded-lg bg-violet-600 hover:bg-violet-700 text-white flex items-center justify-center disabled:opacity-40 transition-colors"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            </button>
          </div>
          <p className="text-[9px] text-slate-400 mt-1 text-center">⌘+I otevři kdekoli · Enter=poslat · Shift+Enter=řádek</p>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
